// Shared fetch wrapper that attaches the app auth bearer token (if present in
// localStorage) to every request, and surfaces 401s so the UI can prompt for
// the token again.

const APP_TOKEN_STORAGE_KEY = 'cg-app-token';
const APP_AUTH_ERROR_HEADER = 'x-app-auth-error';

let onUnauthorizedHandler = null;

export const APP_TOKEN_KEY = APP_TOKEN_STORAGE_KEY;

export function getAppToken() {
    if (typeof window === 'undefined') return '';
    return window.localStorage.getItem(APP_TOKEN_STORAGE_KEY) || '';
}

export function setAppToken(token) {
    if (typeof window === 'undefined') return;
    if (token) {
        window.localStorage.setItem(APP_TOKEN_STORAGE_KEY, token);
    } else {
        window.localStorage.removeItem(APP_TOKEN_STORAGE_KEY);
    }
}

export function onUnauthorized(handler) {
    onUnauthorizedHandler = typeof handler === 'function' ? handler : null;
}

// Detect path-prefix store routing: if the page was loaded at
// /s/<slug>/..., every API call should be sent under that same prefix
// so nginx can inject the X-Active-Store header for the right store.
// Returns '' when no prefix is active (single-store mode), or
// '/s/<slug>' otherwise.
const STORE_SLUG_PATTERN = /^\/s\/([a-z0-9][a-z0-9-]*)(?:\/|$)/;

export function getStorePathPrefix() {
    if (typeof window === 'undefined') return '';
    const match = window.location.pathname.match(STORE_SLUG_PATTERN);
    return match ? `/s/${match[1]}` : '';
}

// Prefix an API path with the current /s/<slug> segment when the page
// is loaded under one. Untouched when the input is an absolute URL or
// when there's no active prefix.
const applyStorePrefix = (input) => {
    if (typeof input !== 'string') return input;
    if (/^https?:\/\//i.test(input)) return input;
    if (!input.startsWith('/')) return input;
    const prefix = getStorePathPrefix();
    if (!prefix) return input;
    if (input.startsWith(`${prefix}/`)) return input;
    return `${prefix}${input}`;
};

export async function apiFetch(input, init = {}) {
    const token = getAppToken();
    const headers = new Headers(init.headers || {});
    if (token && !headers.has('authorization')) {
        headers.set('Authorization', `Bearer ${token}`);
    }
    const response = await fetch(applyStorePrefix(input), {
        ...init,
        credentials: init.credentials || 'include',
        headers,
    });
    if (
        response.status === 401 &&
        response.headers.get(APP_AUTH_ERROR_HEADER) === 'session' &&
        onUnauthorizedHandler
    ) {
        try {
            onUnauthorizedHandler();
        } catch (_err) {
            // The handler is best-effort — don't break the caller.
        }
    }
    return response;
}
